import Trip from '../Trip';
import SuggestionService from '../SuggestionService';
import { VehicleType } from '../types';
import {
    CabinClassRequest,
    type FlightLegDto,
    type FlightsMetaDto,
} from '@clients/FlightApi';
import airlineLogos from '@static/availableAirlineLogos.json';
import { CabinClasses } from '@itinerary/trip/flight/types';

export default class Flight extends Trip {
    override serviceId = 0;
    /**
     * Time of airplane departure from origin, in 'hh:mm' format
     */
    override departureTime = '';

    /**
     * Time of airplane arrival to destination, in 'hh:mm' format
     */
    override arrivalTime = '';

    override stops = 0;

    override origin = '';

    override destination = '';

    description = '';

    id = '';

    /**
     * Indicates the flight type, 0 for charter, 1 for system and 2 for international.
     */
    type = 0;

    override departureDateTime = new Date(0);
    override arrivalDateTime = new Date(0);

    override vehicleType = VehicleType.Plane;

    override originCityName = '';

    override originCode = '';

    override originTerminal = '';

    override destinationCityName = '';

    override destinationCode = '';

    override destinationTerminal = '';

    override key = '';

    duration_ = 0;
    override showCityCodes = false;

    status = '';

    flightNumber = '';

    override serviceNumber = '0';

    override get duration(): number {
        return this.duration_;
    }

    constructor(
        leg: FlightLegDto,
        meta: FlightsMetaDto,
        index: number,
        isDomestic: boolean,
        public cabinClass = CabinClassRequest.Unknown as CabinClassRequest
    ) {
        super();

        if (!leg) {
            return;
        }
        const getLogo = (itaCode: string) => {
            let logoUrl;
            let logoFallbackUrl;
            if (airlineLogos.svg.includes(`${itaCode}.svg`)) {
                logoUrl = `/img/AirlineLogos/svg/${itaCode}.svg`;
                if (airlineLogos.png.includes(`${itaCode}.png`)) {
                    logoFallbackUrl = `/img/AirlineLogos/${itaCode}.png`;
                } else {
                    logoFallbackUrl = '/img/AirlineLogos/default.png';
                }
            } else if (airlineLogos.png.includes(`${itaCode}.png`)) {
                logoUrl = `/img/AirlineLogos/${itaCode}.png`;
                logoFallbackUrl = '/img/AirlineLogos/default.png';
            } else {
                logoUrl = '/img/AirlineLogos/svg/default.svg';
                logoFallbackUrl = '/img/AirlineLogos/default.png';
            }
            return {
                logoUrl,
                logoFallbackUrl,
            };
        };
        this.technicalStops =
            leg?.TechnicalStops?.map(
                ({ DepartureDateTime, Airport, ArrivalDateTime }) => ({
                    message: `توقف فنی در فرودگاه ${Airport} از 
                    ${dayConfig.js(ArrivalDateTime).format(
                        formatter({
                            format: 'long',
                            includeWeekday: true,
                            includeYear: true,
                            includeTime: true,
                        })
                    )} 
                    تا 
                    ${dayConfig.js(DepartureDateTime).format(
                        formatter({
                            format: 'long',
                            includeWeekday: true,
                            includeYear: true,
                            includeTime: true,
                        })
                    )}`,
                })
            ) || [];
        this.carrierCode =
            (leg.OperatingAirline?.IataCode
                ? leg.OperatingAirline?.IataCode
                : leg.Airline?.IataCode) || '';
        const title =
            (leg.OperatingAirline?.IataCode
                ? leg.OperatingAirline?.PersianTitle ||
                  leg.OperatingAirline?.EnglishTitle
                : leg.Airline?.PersianTitle || leg.Airline?.EnglishTitle) || '';
        this.departureTime = dayConfig
            .gregory(leg.DepartureTime?.substring(0, 19))
            .format('HH:mm');
        this.arrivalTime = dayConfig
            .gregory(leg.ArrivalTime?.substring(0, 19))
            .format('HH:mm');
        this.corporationInfo = {
            ...getLogo(this.carrierCode),
            title,
            subTitle: leg.FlightNumber?.toString(),
            code: this.carrierCode,
        };

        if (
            leg.OperatingAirline &&
            leg.OperatingAirline.IataCode &&
            leg.OperatingAirline.IataCode !== leg.Airline?.IataCode
        ) {
            this.operatingWarning = `صدور بلیط این پرواز توسط ${
                leg.Airline?.PersianTitle || leg.Airline?.EnglishTitle
            } و پرواز توسط ${title} انجام می‌شود.`;
        }

        this.origin = leg.Origin + (!isDomestic ? ` (${leg.OriginCode})` : '');
        this.originCityName = leg.Origin || '';
        this.originCode = leg.OriginCode || '';

        this.destination =
            leg.Destination + (!isDomestic ? ` (${leg.DestinationCode})` : '');
        this.destinationCityName = leg.Destination || '';
        this.destinationCode = leg.DestinationCode || '';

        this.showCityCodes = !isDomestic;
        this.serviceNumber = leg.FlightNumber || '';
        this.departureDateTime = dayConfig
            .gregory(leg.DepartureTime?.substring(0, 19))
            .toDate();
        this.arrivalDateTime = dayConfig
            .gregory(leg.ArrivalTime?.substring(0, 19))
            .toDate();

        this.flightNumber = leg.FlightNumber || '';
        this.vehicleInfo = {
            title: 'هواپیما',
            type: leg.AirCraft?.PersianTitle || '',
            code: leg.AirplaneCode || '',
            description: leg.AirCraft?.PersianTitle || '',
        };

        this.id = leg.Id?.toString() || '';
        this.serviceId = leg.Id ?? 0;
        this.duration_ =
            leg.JourneyTime?.match(/(\d+):(\d+):(\d+)/)!
                .slice(1)
                .reduce(
                    (prev, val, index) =>
                        prev + parseInt(val) * 60 ** (2 - index),
                    0
                ) || 0;
        this.stops = leg.Stops || 0;
        this.key = leg.Id?.toString() || '';
        this.status = leg.FlightStatus || '';

        const originAirport =
            leg.OriginAirport ||
            meta.RoutesInfo?.[index]?.Origin?.PersianTitle ||
            leg.Origin;
        const destinationAirport =
            leg.DestinationAirport ||
            meta.RoutesInfo?.[index]?.Destination?.PersianTitle ||
            leg.Destination;
        if (isDomestic) {
            this.originTerminal = `فرودگاه ${originAirport} (${
                this.originCode
            })${
                leg.DepartureTerminal
                    ? ' - ' + `ترمینال ${leg.DepartureTerminal}`
                    : ''
            }`;
            this.destinationTerminal = `فرودگاه ${destinationAirport} (${
                this.destinationCode
            })${
                leg.ArrivalTerminal
                    ? ' - ' + `ترمینال ${leg.ArrivalTerminal}`
                    : ''
            }`;
        } else {
            this.originTerminal = `${leg.OriginAirport} (${this.originCode})`;
            this.destinationTerminal = `${leg.DestinationAirport} (${this.destinationCode})`;
        }
        this.type = 1;
        this.suggestionServices =
            leg.SuggestedServices?.map(
                sug =>
                    new SuggestionService({
                        id: sug.Id || 0,
                        title: sug.Title || '',
                        description: sug.Description || '',
                        price: sug.Price || 0,
                        unitTitle: sug.UnitTitle || '',
                        needDescription: sug.NeedDescription,
                        image: sug.Images?.[0],
                        discount: sug.DiscountAmount || 0,
                        levelRange: sug.ApplicableLevelRange,
                        flightClass:
                            (sug.FlightClass as CabinClassRequest) ?? undefined,
                    })
            ) || [];
    }

    override get tripExtraInfo(): string[] {
        if (
            [CabinClassRequest.Unknown, CabinClassRequest.All].includes(
                this.cabinClass as any
            )
        ) {
            return super.tripExtraInfo;
        }
        const cabinClassName =
            CabinClasses.find(({ Id }) => Id === this.cabinClass)?.Title || '';
        return [...super.tripExtraInfo, cabinClassName];
    }

    override get destinationHasWarning() {
        return false;
    }
}
